<template>
  <div class="ws-versions">
    <div v-if="canUpload || (document && document.isCurrentOverridden && canManage)" class="ws-versions__actions">
      <v-btn v-if="canUpload" small depressed color="primary" :disabled="busy" @click="openAddVersion">
        <v-icon small left>mdi-tray-arrow-up</v-icon>
        Add version
      </v-btn>
      <v-btn
        v-if="document && document.isCurrentOverridden && canManage"
        small
        outlined
        color="primary"
        :loading="busy"
        @click="revertToLatest">
        <v-icon small left>mdi-pin-off-outline</v-icon>
        Revert to latest
      </v-btn>
    </div>

    <div v-if="loading && !versions.length" class="ws-versions__state">
      <v-progress-circular indeterminate size="28" width="3" color="primary" />
    </div>
    <div v-else-if="!versions.length" class="ws-versions__state">No versions</div>

    <ol v-else class="ws-versions__list" aria-label="Versions, newest first">
      <li
        v-for="version in versions"
        :key="version.id"
        class="ws-version"
        :class="{
          'ws-version--selected': version.id === selectedId,
          'ws-version--current': version.isCurrent,
        }">
        <button
          type="button"
          class="ws-version__main"
          :aria-current="version.id === selectedId ? 'true' : null"
          :aria-label="`Show version ${version.versionNumber}${version.isCurrent ? ', current' : ''}`"
          @click="$emit('select', version)">
          <span class="ws-version__sheet" aria-hidden="true">
            <img
              v-if="version.thumbnailUrl && !brokenThumbs[version.id]"
              class="ws-version__thumb"
              :src="version.thumbnailUrl"
              alt=""
              loading="lazy"
              decoding="async"
              @error="onThumbError(version)" />
            <span v-else class="ws-version__ext">{{ extensionOf(version) || 'FILE' }}</span>
            <span class="ws-version__stamp">v{{ version.versionNumber }}</span>
          </span>

          <span class="ws-version__body">
            <span class="ws-version__top">
              <span v-if="version.revisionNumber" class="ws-version__rev">Rev {{ version.revisionNumber }}</span>
              <span v-else class="ws-version__rev ws-version__rev--none">No revision</span>
              <span v-if="version.isCurrent" class="ws-version__badge">
                <v-icon v-if="version.isPinned" x-small color="warning" class="mr-1" title="Pinned as current">mdi-pin</v-icon>Current
              </span>
              <span v-if="version.id === selectedId" class="ws-version__badge ws-version__badge--viewing">Viewing</span>
            </span>
            <span class="ws-version__file" :title="version.fileName">{{ version.fileName }}</span>
            <span class="ws-version__meta">
              <span>{{ $formatDate(version.createDate) }}</span>
              <span v-if="version.createdByName" class="ws-version__who">{{ version.createdByName }}</span>
              <span v-if="version.size" class="ws-version__size">{{ $formatFileSize(version.size, 1) }}</span>
              <span v-if="version.linkCount" class="ws-version__links" title="Links placed on this version">
                {{ linkCountLabel(version.linkCount) }}
              </span>
            </span>
          </span>
        </button>
        <div class="ws-version__tools">
          <v-btn icon x-small :aria-label="`Download version ${version.versionNumber}`" title="Download" @click="downloadVersion(version)">
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn
            v-if="canManage && !version.isCurrent"
            icon
            x-small
            :aria-label="`Set version ${version.versionNumber} as current`"
            title="Set as current"
            :disabled="busy"
            @click="setAsCurrent(version)">
            <v-icon small>mdi-pin-outline</v-icon>
          </v-btn>
        </div>
      </li>
    </ol>

    <div v-if="versions.length < total" class="ws-versions__more">
      <v-btn small text color="primary" :loading="loading" @click="loadPage(page + 1)">Load more</v-btn>
    </div>

    <AddDocumentVersionDialog
      v-model="addVersionOpen"
      :document-id="documentId"
      :document="document"
      :versions="versions"
      :next-version-number="nextVersionNumber" />
  </div>
</template>

<script>
  import documentVersionsService from '../../service/documentVersions.service';
  import { $formatDate, $handleError } from '@/utils';
  import { downloadDocumentVersion } from '@/utils/documentProxyUtils';
  import { documentExtension } from '../../viewer/documentKinds';
  import { isVersionConflict, conflictMessage, expectedNextVersion, linkCountLabel } from '../../utils/documentVersions';
  import AddDocumentVersionDialog from '../AddDocumentVersionDialog.vue';

  const PAGE_SIZE = 20;

  /**
   * Versions of one document in the workspace side panel. Clicking a version shows it in the
   * viewer (`select`); set as current, revert and add version call the API here and report the
   * new DocumentVM with `document-changed`. On a stale rowVersion (409) it asks for a `reload`.
   */
  export default {
    name: 'WorkspaceVersionsTab',
    components: { AddDocumentVersionDialog },
    props: {
      documentId: { type: [String, Number], required: true },
      document: { type: Object, default: null },
      selectedId: { type: [String, Number], default: null },
      canManage: { type: Boolean, default: false },
      canUpload: { type: Boolean, default: false },
    },
    data() {
      return {
        versions: [],
        total: 0,
        page: 0,
        loading: false,
        busy: false,
        // revision id -> version number, also used by the history sentences
        versionNumbers: {},
        addVersionOpen: false,
        // version id -> true when its thumbnail failed to load; the card then shows the file type
        brokenThumbs: {},
        requestSeq: 0,
      };
    },
    computed: {
      nextVersionNumber() {
        return expectedNextVersion(this.versionNumbers);
      },
    },
    watch: {
      documentId: {
        immediate: true,
        handler() {
          this.reload();
        },
      },
    },
    methods: {
      $formatDate,
      linkCountLabel,
      reload() {
        this.versions = [];
        this.total = 0;
        this.page = 0;
        this.versionNumbers = {};
        return this.loadPage(1);
      },
      loadPage(page) {
        // "Load more" while a page is in flight would append the same page twice.
        if (page > 1 && this.loading) return Promise.resolve();
        // A reload or document switch supersedes older requests; their responses are dropped.
        const seq = ++this.requestSeq;
        this.loading = true;
        return documentVersionsService
          .getVersions(this.documentId, { page, itemsPerPage: PAGE_SIZE })
          .then((res) => {
            if (seq !== this.requestSeq) return;
            const items = res.data?.items || [];
            this.versions = page === 1 ? items : this.versions.concat(items);
            this.total = res.data?.total || 0;
            this.page = page;
            const numbers = { ...this.versionNumbers };
            items.forEach((v) => (numbers[v.id] = v.versionNumber));
            this.versionNumbers = numbers;
            this.$emit('loaded', { versions: this.versions, versionNumbers: this.versionNumbers });
          })
          .catch((err) => {
            if (seq !== this.requestSeq) return;
            $handleError(err);
            this.$emit('load-failed');
          })
          .finally(() => {
            if (seq === this.requestSeq) this.loading = false;
          });
      },
      async downloadVersion(version) {
        const ok = await downloadDocumentVersion(this.documentId, version.id, version.fileName, version.storageReference);
        if (!ok) this.$dialog.notify.error('Could not download this version.', { position: 'top-right', timeout: 5000 });
      },
      async setAsCurrent(version) {
        const confirmed = await this.$dialog.confirm({
          title: `Make v${version.versionNumber} current?`,
          text: `Everyone opening this document will see v${version.versionNumber} until it is reverted to the latest version.`,
          actions: { false: 'Cancel', true: { text: 'Set as current', color: 'primary' } },
        });
        if (!confirmed) return;
        this.applyChange(
          () => documentVersionsService.setCurrent(this.documentId, version.id, this.document?.rowVersion),
          `v${version.versionNumber} is now the current version.`,
        );
      },
      async revertToLatest() {
        const confirmed = await this.$dialog.confirm({
          title: 'Revert to the latest version?',
          text: 'The pin is removed and the highest version becomes current.',
          actions: { false: 'Cancel', true: { text: 'Revert', color: 'primary' } },
        });
        if (!confirmed) return;
        this.applyChange(
          () => documentVersionsService.clearCurrent(this.documentId, this.document?.rowVersion),
          'The latest version is current again.',
        );
      },
      applyChange(request, successText) {
        this.busy = true;
        return request()
          .then((res) => {
            this.$dialog.notify.success(successText, { position: 'top-right', timeout: 3000 });
            this.$emit('document-changed', res.data);
            return this.reload();
          })
          .catch((err) => {
            if (isVersionConflict(err)) {
              this.$dialog.notify.warning(`${conflictMessage(err)} The latest state has been loaded.`, {
                position: 'top-right',
                timeout: 6000,
              });
              this.$emit('reload');
              return this.reload();
            }
            $handleError(err);
          })
          .finally(() => {
            this.busy = false;
          });
      },
      openAddVersion() {
        this.addVersionOpen = true;
      },
      extensionOf(version) {
        return documentExtension(version).slice(0, 4).toUpperCase();
      },
      onThumbError(version) {
        this.$set(this.brokenThumbs, version.id, true);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ws-versions__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 12px 4px;
  }

  .ws-versions__state {
    display: flex;
    justify-content: center;
    padding: 32px 12px;
    opacity: 0.75;
  }

  // Revision spine: the versions are a sequence, so each card's sheet hangs on one vertical line,
  // newest at the top. The line runs through the sheets' centres (left padding + half the sheet).
  $sheet-w: 44px;
  $sheet-h: 56px;
  $card-pad: 8px;
  $card-gap: 4px;
  $spine-x: $card-pad + 22px; // card padding + half the sheet width

  .ws-versions__list {
    position: relative;
    list-style: none;
    margin: 0;
    padding: 8px;
  }

  .ws-version {
    position: relative;
    display: flex;
    align-items: stretch;
    gap: 2px;
    border: 1px solid transparent;
    border-radius: var(--ds-radius-md, 8px);
    transition: background-color 120ms ease, border-color 120ms ease;

    // The spine segment between this card and the next one down.
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      left: $spine-x;
      top: $card-pad + $sheet-h;
      bottom: -($card-pad + $card-gap);
      width: 2px;
      margin-left: -1px;
      background: var(--ds-border-default);
      pointer-events: none;
    }

    & + & {
      margin-top: $card-gap;
    }

    &:hover {
      background: var(--ds-surface-hover);
    }

    &--selected {
      border-color: var(--v-primary-base);
      background: var(--ds-surface-active);

      &:hover {
        background: var(--ds-surface-active);
      }
    }

    &--current:not(:last-child)::after {
      background: var(--v-primary-base);
      opacity: 0.45;
    }
  }

  .ws-version__main {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: $card-pad;
    text-align: left;
    color: inherit;
    border-radius: var(--ds-radius-md, 8px);

    &:focus-visible {
      outline: 2px solid var(--v-primary-base);
      outline-offset: -2px;
    }
  }

  // A sheet of paper: the first page, cropped from the top where drawings carry their title.
  .ws-version__sheet {
    position: relative;
    z-index: 1; // above the spine
    flex: 0 0 $sheet-w;
    width: $sheet-w;
    height: $sheet-h;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    background: var(--v-bg1-base, #f4f6f5);
    border: 1px solid var(--ds-border-default);
    box-shadow: var(--ds-shadow-1);
    overflow: hidden;

    .ws-version--current & {
      border-color: var(--v-primary-base);
    }
  }

  .ws-version__thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    background: #fff;
  }

  .ws-version__ext {
    margin-bottom: 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    opacity: 0.55;
  }

  // Version number stamped on the sheet's corner, like a title block.
  .ws-version__stamp {
    position: absolute;
    left: 0;
    bottom: 0;
    min-width: 22px;
    padding: 1px 5px;
    border-top-right-radius: 3px;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    font-variant-numeric: tabular-nums;
    color: var(--v-primary-base);
    background: var(--ds-surface-elevated, #fff);
    border-top: 1px solid var(--ds-border-default);
    border-right: 1px solid var(--ds-border-default);

    .ws-version--current & {
      color: #fff;
      background: var(--v-primary-base);
      border-color: var(--v-primary-base);
    }
  }

  .ws-version__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 1px;
  }

  .ws-version__top {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 18px;
  }

  .ws-version__rev {
    font-family: var(--ds-font-family-heading);
    font-size: var(--ds-font-body-sm, 13px);
    font-weight: 600;

    &--none {
      font-weight: 400;
      opacity: 0.55;
    }
  }

  .ws-version__badge {
    display: inline-flex;
    align-items: center;
    font-size: var(--ds-font-micro, 10px);
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-primary-base);

    &--viewing {
      margin-left: auto;
      opacity: 0.75;
    }
  }

  .ws-version__file {
    font-size: var(--ds-font-body-sm, 13px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ws-version__meta {
    display: flex;
    flex-wrap: wrap;
    column-gap: 6px;
    font-size: var(--ds-font-caption, 12px);
    opacity: 0.65;
    font-variant-numeric: tabular-nums;

    > span + span::before {
      content: '·';
      margin-right: 6px;
    }
  }

  .ws-version__tools {
    display: flex;
    flex-direction: column;
    padding: 6px 6px 0 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .ws-version {
      transition: none;
    }
  }

  .ws-versions__more {
    display: flex;
    justify-content: center;
    padding-bottom: 12px;
  }
</style>
